import React, { useState, useRef, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import SendIcon from "@mui/icons-material/Send";
import catBotLogo from "../../assets/catbot.png";
import axios from "axios";
import Markdown from "react-markdown";
import { Resizable, ResizableBox } from "react-resizable";

const CatBot = () => {
  const [ dialogPosition, setDialogPosition] = useState({
    x: 0,
    y: window.innerHeight - 570,
  });
  const [size, setSize] = useState({ width: 520, height: 400 });
  const [isDragging, setIsDragging] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      message:
        "👋 Hi there! I'm CatBot, your AI assistant from RedCat Imagine, the home of premium refurbished mobile phones. 🏆\n\nI'm here to help retailers like you with everything related to RedCat phones. Need pricing details, specs, or recommendations? Just ask! 🚀",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const chatEndRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dialogOffsetRef = useRef({ x: 0, y: 0 });

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Icon drag handlers

  const handleDialogMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input")) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dialogOffsetRef.current = { ...dialogPosition };
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setDialogPosition((prev) => ({
        x: dialogOffsetRef.current.x + (e.clientX - dragStartRef.current.x),
        y: dialogOffsetRef.current.y + (e.clientY - dragStartRef.current.y),
      }));
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const toggleChat = () => setIsOpen((prev) => !prev);

  const sendMessage = async (e) => {
    e.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    const userChat = {
      message: trimmedInput,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prevMessages) => [...prevMessages, userChat]);
    setIsTyping(true);
    setInput("");

    try {
      const response = await axios.post(
        "https://primary-production-352e.up.railway.app/webhook/7595bf17-7894-418c-8b20-5b3d0575e5d8",
        { message: trimmedInput, userID: "123456" },
        { headers: { "Content-Type": "application/json" } }
      );

      setMessages((prev) => [
        ...prev,
        {
          message:
            response.status === 200
              ? response.data.trim()
              : "⚠️ Oops! Connection error. Try again.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          message:
            "⚠️ Something went wrong, but don't worry—our team is on it! Try again later.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };
  const onResize = (event, { size }) => {
    setSize({ width: size.width, height: size.height });
  };
  return (
    <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-50">
      {/* Draggable icon button */}
      <div
        className="absolute bottom-7 left-7 w-14 h-14 flex items-center justify-center bg-black text-white rounded-full shadow-lg border-2 border-white cursor-pointer pointer-events-auto"
        // style={{
        //   // top: `${iconPosition.y}px`,
        //   // left: `${iconPosition.x}px`,
        //   bottom:"28px",
        //   left:"7px",
        //   pointerEvents: "auto",
        //   cursor: "pointer",
        //   zIndex: 99999,
        //   borderColor: "white",
        //   borderStyle: "solid",
        //   borderWidth: "2px",
        // }}
        onClick={toggleChat}
      >
        {isOpen ? (
          <X size={24} />
        ) : (
          <img src={catBotLogo} alt="Chat" className="h-12 w-12" />
        )}
      </div>

      {/* Draggable Chat interface */}
      {isOpen && (
        <Resizable
          height={size.height}
          width={size.width}
          onResize={onResize}
        >
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 pointer-events-auto"
            style={{
              top: `${dialogPosition.y}px`,
              left: `${dialogPosition.x}px`,
              width: `${size.width}px`,
              height: `${size.height}px`,
              // width: "520px",
              // pointerEvents: "auto",
              // zIndex: 99999,
            }}
          >
            {/* Chat header - draggable area */}
            <div
              className="bg-black text-white p-4 flex items-center justify-between cursor-grab"
              onMouseDown={handleDialogMouseDown}
              // style={{ cursor: isDraggingDialog ? "grabbing" : "grab" }}
            >
              <div className="flex items-center space-x-2">
                <img src={catBotLogo} alt="Chat" className="h-12 w-12" />
                <span className="font-medium">CatBot</span>
              </div>
              <button
                className="text-white hover:text-gray-200 w-auto"
                onClick={toggleChat}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 h-96 overflow-y-auto bg-gray-50 flex flex-col space-y-2">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg max-w-xs ${
                    msg.isUser
                      ? "bg-[red] text-white self-end"
                      : "bg-gray-200 text-black self-start"
                  }`}
                >
                  <Markdown>{msg.message}</Markdown>
                </div>
              ))}
              {isTyping && (
                <div className="typing-indicator self-start text-gray-500">
                  CatBot is typing...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {/* Chat input */}
            <form
              onSubmit={sendMessage}
              className="border-t border-gray-200 p-4 flex"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-300 text-black t-border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
              />
              <button
                type="submit"
                className="bg-red text-white rounded-full py-2 hover:bg-red-900 flex items-center justify-center w-16 ml-2"
              >
                <SendIcon size={18} />
              </button>
            </form>
          </div>
        </Resizable>
      )}
    </div>
  );
};

export default CatBot;
